import React, { lazy, useCallback, useState } from 'react';
import classNames from 'classnames';
import './TrackOrders.scss';
import type { MostRecentModel } from '@/common/interfaces/mostRecent.interface';
import Overlay from '@/components/Overlay';
import { getOrderDetailPath } from '@/constants/Order';
import { useTrackOrderUrlSync } from '@/hooks/useTrackOrderUrlSync';
import {
  defCloseIconWrapperStyles,
  defHalfOverlayStyles,
  defaultConfirmModalStyles,
  defaultModalStyles,
  defaultOverlayStyles,
  halfOverlayCloseWrapStyles,
} from '@/components/utils/DefaultStyles';
import { useAppDispatch } from '@/store/hooks';
import { setShowParticipantsOverlay, setShowTrackOrderdetails } from '@/store/reducers/order';
import useViewport from '@/common/context/ViewPort/useViewPort';

const IndivOrderTrackTable = lazy(() => import('./IndivOrderTrackTable/IndivOrderTrackTable'));
const BulkOrderTrackTable = lazy(() => import('./BulkOrderTrackTable/BulkOrderTrackTable'));

const bulkOrderTrackTableStyles = {
  padding: '50px 0',
  width: '820px',
  height: '550px',
};

const indivOrderTrackTableStyles = {
  padding: '30px 0',
};

const indivOrderTableCloseStyles = {
  padding: '65px 65px 0px 0px',
};

const participantSearchCloseStyles = {
  padding: '30px 30px 0px 0px',
};

const partcipnatTableStyles = {
  padding: '30px 0 0 0',
  left: '50%',
  width: '800px',
  transform: 'translate(-50%, 0)',
};

interface ITrackOrders {
  mostRecentData: MostRecentModel;
  groupOrderDate?: string | null;
  isNewGroupOrder?: boolean;
  individualShipping?: boolean;
  showParticipantsOverlay: boolean;
  id: string | number;
  /** Id used in the `/track/:orderId` url when it differs from `id` (e.g. participant id). */
  trackRouteId?: string | number;
  /** Where to land on close when the overlay was opened directly via `/track/:orderId`. */
  closeRedirectPath?: string;
  onTrackOrdersClose?: () => void;
}

const TrackOrders: React.FC<ITrackOrders> = ({ ...props }: ITrackOrders) => {
  const {
    individualShipping = false,
    id,
    trackRouteId,
    closeRedirectPath,
    showParticipantsOverlay = false,
    onTrackOrdersClose,
  } = props;
  const dispatch = useAppDispatch();
  const [isShow, setShow] = useState(true);
  const screen = useViewport(true);

  let defModalStyles = {};
  let defCloseIconStyles = { ...defCloseIconWrapperStyles };

  const closeOverlay = useCallback(() => {
    setShow(false);
    dispatch(setShowTrackOrderdetails(false));
    dispatch(setShowParticipantsOverlay(false));

    if (onTrackOrdersClose) {
      onTrackOrdersClose();
    }
  }, [dispatch, onTrackOrdersClose]);

  const routeId = trackRouteId ?? id;
  const { closeTrackOrderUrl } = useTrackOrderUrlSync({
    orderId: routeId,
    // The participant search overlay reuses this component but is not order tracking.
    enabled: !showParticipantsOverlay,
    closeRedirectPath: closeRedirectPath ?? getOrderDetailPath(routeId),
    onLeaveTrackRoute: closeOverlay,
  });

  const closeClicked = () => {
    closeOverlay();
    closeTrackOrderUrl();
  };

  if (showParticipantsOverlay) {
    defModalStyles = {
      ...defHalfOverlayStyles,
      ...indivOrderTrackTableStyles,
      ...partcipnatTableStyles,
    };
    defCloseIconStyles = { ...halfOverlayCloseWrapStyles, ...participantSearchCloseStyles };
  } else if (individualShipping) {
    defModalStyles = { ...defHalfOverlayStyles, ...indivOrderTrackTableStyles };
    defCloseIconStyles = { ...halfOverlayCloseWrapStyles, ...indivOrderTableCloseStyles };
  } else {
    defModalStyles = {
      ...defaultModalStyles,
      ...defaultConfirmModalStyles,
      ...bulkOrderTrackTableStyles,
    };
  }

  const indivClassName = `indiv-track-order-participant-overlay--${
    screen === 'mobile' || screen === 'tabSmall' ? 'mobile' : 'desktop'
  }`;

  const bulkClassName = `bulk-track-order-participant-overlay--${screen}`;

  const modalClassName = classNames({
    'indiv-track-order-participant-overlay': individualShipping || showParticipantsOverlay,
    [indivClassName]: individualShipping || showParticipantsOverlay,
    [bulkClassName]: !individualShipping && !showParticipantsOverlay,
  });

  return (
    <Overlay
      modalStyles={defModalStyles}
      closeIconPath="/src/assets/svg/close.svg"
      closeIconWrapperStyles={defCloseIconStyles}
      closeIconWrapperClass="close-part-search-overlay"
      overlayStyles={defaultOverlayStyles}
      isOpen={isShow}
      onCloseClick={closeClicked}
      onBackdropClick={closeClicked}
      modalClassName={modalClassName}
    >
      {!showParticipantsOverlay && (
        <div data-testid="order-detail-tracking">
          {!individualShipping ? (
            <BulkOrderTrackTable mostRecentData={props.mostRecentData} />
          ) : (
            <IndivOrderTrackTable
              mostRecentData={props.mostRecentData}
              orderId={id}
              isPartcipantSearchOnly={false}
            />
          )}
        </div>
      )}

      {showParticipantsOverlay && (
        <IndivOrderTrackTable
          mostRecentData={props.mostRecentData}
          orderId={id}
          isPartcipantSearchOnly
        />
      )}
    </Overlay>
  );
};

export default TrackOrders;
